/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Select, InputPrice, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {basicLookup} from '../../../redux/slices/lookup.slice'
import {paymentRequestGetId, paymentRequestUpdate, setRedirect} from '../../../redux/slices/paymentrequest.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  PaymentRequestType: state.lookup.PaymentRequestType,
  loading: state.lookup.loading
})

const selectPaymentRequestData = (state) => ({
  isRedirect: state.paymentrequest.isRedirect,
  detail: state.paymentrequest.detail,
  loading: state.paymentrequest.loading
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {PaymentRequestType, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectPaymentRequestData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(basicLookup('PaymentRequestType'))
    dispatch(paymentRequestGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      CompanyTitle: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      CompanyBalance: Yup.number().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      Iban: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      RequestedPayment: Yup.number().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      RequestedDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      PaymentRequestTypeId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    CompanyTitle: '',
    CompanyBalance: 0,
    IdentityNumber: '',
    TaxNumber: '',
    Iban: '',
    RequestedPayment: 0,
    RequestedDate: new Date(),
    PaymentRequestTypeId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(paymentRequestUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    enableReinitialize: true,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.PAYMENTREQUEST'}),
      path: '/expenses/payment-request',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/expenses/payment-request/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (detail) {
      formik.setFieldValue('Id', detail.id || '')
      formik.setFieldValue('Name', detail.name || '')
      formik.setFieldValue('CompanyTitle', detail.companyTitle || '')
      formik.setFieldValue('CompanyBalance', detail.companyBalance || 0)
      formik.setFieldValue('IdentityNumber', detail.identityNumber || '')
      formik.setFieldValue('TaxNumber', detail.taxNumber || '')
      formik.setFieldValue('Iban', detail.iban || '')
      formik.setFieldValue('RequestedPayment', detail.requestedPayment || 0)
      formik.setFieldValue('RequestedDate', detail.requestedDate ? new Date(detail.requestedDate) : new Date())
      formik.setFieldValue('PaymentRequestTypeId', detail.paymentRequestTypeId || '')
    }
  }, [detail])

  useEffect(() => {
    if (isRedirect && typeof isRedirect === 'object' && isRedirect.Id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.Id}`)
      dispatch(setRedirect(false))
    }
  }, [isRedirect, navigate, pageUrl, dispatch])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.PAYMENTREQUEST.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.COMPANYTITLE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('CompanyTitle')}
                  />
                  {formik.touched.CompanyTitle && formik.errors.CompanyTitle && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.CompanyTitle}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.COMPANYBALANCE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('CompanyBalance')}
                    onChange={(value) => formik.setFieldValue('CompanyBalance', Number(value))}
                  />
                  {formik.touched.CompanyBalance && formik.errors.CompanyBalance && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.CompanyBalance}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.IDENTITYNUMBER'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    maxLength={11}
                    {...formik.getFieldProps('IdentityNumber')}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.TAXNUMBER'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('TaxNumber')}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.IBAN'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Iban')}
                  />

                  {formik.touched.Iban && formik.errors.Iban && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Iban}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.REQUESTEDPAYMENT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('RequestedPayment')}
                    onChange={(value) => formik.setFieldValue('RequestedPayment', Number(value))}
                  />

                  {formik.touched.RequestedPayment && formik.errors.RequestedPayment && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.RequestedPayment}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.REQUESTEDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('RequestedDate')}
                    onChange={([date]) => formik.setFieldValue('RequestedDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.PAYMENTREQUESTTYPEID'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={PaymentRequestType}
                    required
                    {...formik.getFieldProps('PaymentRequestTypeId')}
                    onChange={(item) =>
                      formik.setFieldValue('PaymentRequestTypeId', Number(item && item.value))
                    }
                  />

                  {formik.touched.PaymentRequestTypeId && formik.errors.PaymentRequestTypeId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.PaymentRequestTypeId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
